import React from 'react';
import numbro from 'numbro';
import { AdvancePaymentsInterface, InvoicePresenter } from '@common/modelDefinition';
import { InvoiceTypeEnum } from '../checkout/interfaces';
import useStore from '@data/state/zustand';
import { MethodOfPaymentEnum } from './InvoiceHeaderView';

const invoiceTypeEnum = {
    geniue: 0,
    advance: 1,
    proforma: 2,
};

const InvoiceItemsSummary = (props: InvoicePresenter) => {
    const { invoice, translate, invoicePreview } = props;
    const {
        currency,
        exchangeRates,
        totalValue,
        deliveredValue,
        invoiceType,
        advancePaymentsValue,
        generalDiscountValue,
        invoiceValue,
        advancePayments,
        reclamation,
        generalDiscountPercentage,
        defaultCurrency,
    } = invoice;

    const invoicingSettings = useStore.getState().invoicingSettings;

    const { includeAccommodationTax } = invoicingSettings;

    const exchangeRatesWhenInvoiceIssued: any = exchangeRates ? JSON.parse(exchangeRates) : {};
    const currencyExchangeRate: number = exchangeRatesWhenInvoiceIssued[currency]
        ? Number(exchangeRatesWhenInvoiceIssued[currency])
        : 1;

    const invoiceValueWithGeneralDiscount: number =
        reclamation !== 2
            ? Number(invoiceValue) - Number(generalDiscountValue)
            : Number(invoiceValue) + Number(generalDiscountValue); // Vraćamo izbijeni popust, sada je invoiceValue negativnog predznaka kod storno računa

    const totalAdvance = advancePaymentsValue;

    const fundsReceived = null;

    return (
        <div className="">
            <div className="row mt-2">
                <div className="col-6">
                    {invoiceType === invoiceTypeEnum.geniue && invoice.id ? (
                        <React.Fragment>
                            <div>
                                <small>
                                    <small>
                                        {translate('Total delivered value')}:{' '}
                                        {deliveredValue
                                            ? numbro(deliveredValue * currencyExchangeRate).format({
                                                  mantissa: 2,
                                                  trimMantissa: false,
                                                  thousandSeparated: true,
                                              })
                                            : ''}{' '}
                                        {currency}
                                    </small>
                                </small>
                            </div>
                            {advancePayments?.length > 0 && (
                                <AdvancePayments {...props} currencyExchangeRate={currencyExchangeRate} />
                            )}
                            {generalDiscountPercentage ? (
                                includeAccommodationTax === 0 || // boravišna uključena na račun i predračun
                                (includeAccommodationTax === 1 && (invoiceType === InvoiceTypeEnum.geniue || invoiceType === InvoiceTypeEnum.proforma)) || // boravišna uključena na račun
                                includeAccommodationTax === 2 ? ( // boravišna uključena na sve
                                    <div className="text-justify">
                                        <small>
                                            <small>
                                                <b>(*)</b> {translate('In accordance with the Law, the accommodation tax must be charged at the rate prescribed by Law, and the general invoice discount does not apply to it.')}
                                            </small>
                                        </small>
                                    </div>
                                ) : null
                            ) : null}
                        </React.Fragment>
                    ) : null}
                </div>

                <div className="col-6 text-right">
                    {invoiceType !== invoiceTypeEnum.advance && invoice.id ? (
                        <div>
                            <div>
                                {translate('Invoice value')}:{' '}
                                {invoiceValue
                                    ? numbro(invoiceValue * currencyExchangeRate).format({
                                          mantissa: 2,
                                          trimMantissa: false,
                                          thousandSeparated: true,
                                      })
                                    : ''}{' '}
                                {currency}
                            </div>

                            <GeneralDiscount
                                {...props}
                                invoiceValueWithGeneralDiscount={invoiceValueWithGeneralDiscount}
                                currencyExchangeRate={currencyExchangeRate}
                            />
                        </div>
                    ) : null}

                    {totalAdvance && totalAdvance > 0 && invoiceType !== invoiceTypeEnum.advance && !invoicePreview ? (
                        <div>
                            {translate('Advance payments')}:{reclamation !== 2 ? ' -' : ' '}
                            {numbro(totalAdvance * currencyExchangeRate).format({
                                mantissa: 2,
                                trimMantissa: false,
                                thousandSeparated: true,
                            })}{' '}
                            {currency}
                            <hr className="m-0 mb-3" />
                        </div>
                    ) : null}
                    {fundsReceived && invoiceType !== invoiceTypeEnum.advance && !invoicePreview ? (
                        <div>
                            {translate('Funds received')}:{reclamation !== 2 ? ' ' : ' -'}
                            {numbro(fundsReceived * currencyExchangeRate).format({
                                mantissa: 2,
                                trimMantissa: false,
                                thousandSeparated: true,
                            })}{' '}
                            {currency}
                            <hr className="m-0 mb-3" />
                        </div>
                    ) : null}

                    <h5>
                        {translate('Total')}:{' '}
                        {totalValue !== null
                            ? numbro(totalValue * currencyExchangeRate).format({
                                  mantissa: 2,
                                  trimMantissa: false,
                                  thousandSeparated: true,
                              })
                            : ''}{' '}
                        {currency}
                    </h5>
                    {defaultCurrency !== currency ? (
                        <h6>
                            {translate('Total')} {defaultCurrency}:{' '}
                            {totalValue !== null
                                ? numbro(totalValue).format({
                                      mantissa: 2,
                                      trimMantissa: false,
                                      thousandSeparated: true,
                                  })
                                : ''}{' '}
                            {defaultCurrency}
                        </h6>
                    ) : null}
                    <hr
                        style={
                            invoice.id
                                ? { borderTop: 'solid 2px black' }
                                : { borderTop: 'solid 2px rgba(0, 0, 0, 0.4)' }
                        }
                    />
                </div>
            </div>
        </div>
    );
};

interface InvoiceItemsSummaryProps extends InvoicePresenter {
    invoiceValueWithGeneralDiscount?: number;
    currencyExchangeRate: number;
}
const GeneralDiscount = (props: InvoiceItemsSummaryProps) => {
    const { invoice, translate, invoiceValueWithGeneralDiscount } = props;

    const {
        generalDiscountValue,
        currencyExchangeRate,
        currency,
        reclamation, // Ako je storno računa ne oduzimamo generalni popust nego ga vraćamo
        generalDiscountPercentage,
    } = invoice;
    if (generalDiscountValue === 0) {
        return null;
    }
    return (
        <div>
            <div>
                (*) {translate('Invoice discount')}: {generalDiscountPercentage}%
            </div>
            <div>
                {translate('Discount value')}:{reclamation !== 2 ? ' -' : ' '}
                {numbro(generalDiscountValue * currencyExchangeRate).format({
                    mantissa: 2,
                    trimMantissa: false,
                    thousandSeparated: true,
                })}{' '}
                {currency}
            </div>

            <hr className="m-0" />

            <div>
                <b>
                    {invoiceValueWithGeneralDiscount
                        ? numbro(invoiceValueWithGeneralDiscount * currencyExchangeRate).format({
                              mantissa: 2,
                              trimMantissa: false,
                              thousandSeparated: true,
                          })
                        : null}{' '}
                    {currency}
                </b>
            </div>
        </div>
    );
};

const AdvancePayments = (props: InvoiceItemsSummaryProps) => {
    const { invoice, translate, currencyExchangeRate, invoicePreview } = props;
    const { advancePayments, currency } = invoice;
    if (invoicePreview) {
        // ne prikazuj na naknadnu potrošnju, na nju ne izdajemo avansne račune
        return null;
    }
    return (
        <ul
            className="oldListGroup oldListGroup-flush mt-2 mb-2 border-bottom border-top"
            style={{ listStyle: 'none' }}
        >
            <li className="oldListGroup-item p-1 text-bold">
                <small className="">{translate('Previous advance payments')}</small>
            </li>
            <small>
                <li className="oldListGroup-item p-1 ">
                    <div className="row d-flex justify-content-between">
                        {/* <div className="col-3">
                            <small>
                                <b>{translate('Name')}</b>
                            </small>
                        </div> */}
                        <div className="col-4">
                            <small>
                                <b>{translate('Invoice nr')}</b>
                            </small>
                        </div>
                        <div className="col-3">
                            <small>
                                <b>{translate('Total')}</b>
                            </small>
                        </div>
                        <div className="col-3">
                            <small>
                                <b>{translate('Total VAT')}</b>
                            </small>
                        </div>
                        <div className="col-2">
                            <small>
                                <b>{translate('Payment type')}</b>
                            </small>
                        </div>
                    </div>
                </li>
            </small>
            <small>
                {advancePayments?.map((a: AdvancePaymentsInterface) => {
                    return (
                        <li key={a.id} className="oldListGroup-item p-1 ">
                            <div className="row d-flex justify-content-between">
                                {/* <div className="col-3">
                                    <small>{a.itemName}</small>
                                </div> */}
                                <div className="col-4">
                                    <small>{a.invoiceNumber}</small>
                                </div>
                                <div className="col-3">
                                    <small>
                                        {numbro(a.paymentAmount * currencyExchangeRate).format({
                                            mantissa: 2,
                                            trimMantissa: false,
                                            thousandSeparated: true,
                                        })}
                                        {currency}
                                    </small>
                                </div>
                                <div className="col-3">
                                    <small>
                                        {a?.totalVAT ? `${a.totalVAT.toFixed(2)} ${currency}` : a.invoiceNumber}
                                    </small>
                                </div>
                                <div className="col-2">
                                    <small>
                                        {translate(MethodOfPaymentEnum[a.typeOfPayment as keyof typeof MethodOfPaymentEnum])}
                                    </small>
                                </div>
                            </div>
                        </li>
                    );
                })}
            </small>
        </ul>
    );
};

const NoteAndReclamation = (props: InvoicePresenter) => {
    const { invoice, translate } = props;
    const { note, reclamationReason, vatRegistered /*generalDiscountPercentage*/ } = invoice;

    const invoicingSettings = useStore.getState().invoicingSettings;

    const invoiceNote: string = invoicingSettings && invoicingSettings.taxFreeNote ? invoicingSettings.taxFreeNote : '';

    const notePreview = note?.split('\n').map((note, key) => {
        return (
            <p className="m-0" key={key}>
                {note}
            </p>
        );
    });
    // Prilagoditi napomenu odabranoj državi
    return (
        <div className="col-12 m-0 p-0 mt-4">
            {!vatRegistered && (
                <div>
                    <small>
                        <b>{translate('Note')}:</b> {translate(invoiceNote)}
                    </small>
                </div>
            )}
            {reclamationReason && (
                <div>
                    <small>
                        <b>{translate('Reclamation reason')}:</b> {reclamationReason}
                    </small>
                </div>
            )}

            {note && (
                <div>
                    <small>
                        <b>{translate('Note')}:</b>
                        {notePreview}
                    </small>
                </div>
            )}
        </div>
    );
};

export { NoteAndReclamation };

export default InvoiceItemsSummary;
